import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { ScrollArea } from '../ui/scroll-area';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useAppSelector, useAppDispatch } from '../../redux/store';
import { DBKnowledgebaseThunks } from '../../redux/thunks/DBServiceThunks';
import { selectLegalDomainKnowledgebases } from '../../redux/selectors/knowledgebaseSelectors';
import { JurisdictionCode } from '../../common/jurisdictions';
import {
  toggleKnowledgebase,
  selectAllKnowledgebases,
  selectNoKnowledgebases,
  setSearchTerm
} from '../../redux/slices/documentSearchSlice';

interface KnowledgebaseSelectorProps {
  jurisdiction: JurisdictionCode;
}

/**
 * KnowledgebaseSelector Component
 * Fully controlled multi-select component for choosing knowledgebases
 * State is managed via Redux store
 */
const KnowledgebaseSelector: React.FC<KnowledgebaseSelectorProps> = ({
  jurisdiction
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<'court' | 'legislation'>('court');

  // Get state from Redux
  const { selectedKnowledgebaseIds: selectedIds, searchTerm } = useAppSelector(state => state.documentSearch);

  // Read directly from Redux using selector
  const knowledgebases = useAppSelector(state =>
    selectLegalDomainKnowledgebases(state, jurisdiction)
  );

  const isLoading = useAppSelector(state => state.DBKnowledgebaseReducer.itemsLoading);

  // Split by isCourt
  const courtKBs = React.useMemo(() => knowledgebases.filter(kb => kb.isCourt === true), [knowledgebases]);
  const legislationKBs = React.useMemo(() => knowledgebases.filter(kb => kb.isCourt !== true), [knowledgebases]);
  const activeKBs = activeTab === 'court' ? courtKBs : legislationKBs;

  // Fetch KBs on mount or when jurisdiction changes
  useEffect(() => {
    dispatch(DBKnowledgebaseThunks.find({
      jurisdiction: jurisdiction.toUpperCase(),
      $limit: 1000,
      $fetchJurisdictionKnowledgebases: true
    }));
  }, [jurisdiction, dispatch]);

  // Filter knowledgebases based on search term
  const filteredKnowledgebases = React.useMemo(() => {
    if (!searchTerm) return activeKBs;
    return activeKBs.filter(kb =>
      kb.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      kb.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [activeKBs, searchTerm]);

  // Handlers dispatch to Redux store
  const handleToggle = (kbId: string) => {
    dispatch(toggleKnowledgebase(kbId));
  };

  const handleSelectAll = () => {
    const allIds = activeKBs.map(kb => kb._id || '');
    dispatch(selectAllKnowledgebases(allIds));
  };

  const handleSelectNone = () => {
    dispatch(selectNoKnowledgebases(activeKBs.map(kb => kb._id || '')));
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (knowledgebases.length === 0) {
    return (
      <div className="text-muted-foreground text-sm p-4 bg-muted/50 rounded-md text-center">
        {t('components.documentSearch.noKnowledgebases')}
      </div>
    );
  }

  const activeTabSelectedCount = activeKBs.filter(kb => selectedIds.includes(kb._id || '')).length;

  return (
    <div className="space-y-3">
      {/* Court / Legislation tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'court' | 'legislation')}>
        <TabsList className="w-full">
          <TabsTrigger value="court" className="flex-1">
            {t('components.documentSearch.courtDecisionsTab')}
            {courtKBs.length > 0 && (
              <span className="ml-1.5 text-xs opacity-60">({courtKBs.length})</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="legislation" className="flex-1">
            {t('components.documentSearch.legislationTab')}
            {legislationKBs.length > 0 && (
              <span className="ml-1.5 text-xs opacity-60">({legislationKBs.length})</span>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t('components.documentSearch.searchKnowledgebases')}
          value={searchTerm}
          onChange={(e) => dispatch(setSearchTerm(e.target.value))}
          className="pl-9"
        />
      </div>

      {/* Select all/none + count */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            disabled={activeTabSelectedCount === activeKBs.length}
          >
            {t('components.documentSearch.selectAll')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSelectNone}
            disabled={activeTabSelectedCount === 0}
          >
            {t('components.documentSearch.selectNone')}
          </Button>
        </div>
        <span className="text-xs text-muted-foreground">
          {t('components.documentSearch.selectedCount')
            .replace('{{count}}', selectedIds.length.toString())
            .replace('{{total}}', knowledgebases.length.toString())}
        </span>
      </div>

      {/* Knowledgebase list */}
      {filteredKnowledgebases.length === 0 ? (
        <div className="text-muted-foreground text-sm p-4 bg-muted/50 rounded-md text-center">
          {t('components.documentSearch.noKnowledgebases')}
        </div>
      ) : (
        <ScrollArea className="h-56 border rounded-md">
          <div className="p-3 space-y-1.5">
            {filteredKnowledgebases.map(kb => {
              const kbId = kb._id || '';
              const isChecked = selectedIds.includes(kbId);
              return (
                <div
                  key={kbId}
                  className="flex items-start space-x-2 p-2 hover:bg-muted/30 rounded-md cursor-pointer"
                  onClick={() => handleToggle(kbId)}
                >
                  <Checkbox
                    id={`kb-${kbId}`}
                    checked={isChecked}
                    onCheckedChange={() => handleToggle(kbId)}
                    className="mt-0.5"
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{kb.name}</div>
                    {kb.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {kb.description}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

export default KnowledgebaseSelector;

